import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { numericAsNumber } from '../../custom-type'
import { user } from '../auth'
import { PriceTypeEnum } from './enums'

export const Price = pgTable(
  'price',
  {
    id: uuid().primaryKey().defaultRandom(),
    name: varchar({ length: 256 }).notNull(),
    price: numericAsNumber().notNull(),
    duration: integer().notNull(),
    /**
     * Volume tier this rate applies to, inclusive on both ends. `tierMaxSeats` null means
     * open-ended ("50+ seats").
     *
     * A plan is therefore several `Price` rows — one per tier — sharing a `duration`. Kept on
     * `Price` rather than in a `price_tier` table so a row still IS a rate: `Invoice.priceId`,
     * `VoucherToPrice` and the admin CRUD all address one, and splitting the rate off would make
     * every one of them point at something that no longer has a price.
     *
     * Consequence worth knowing: a voucher must be attached to EVERY tier row of a plan, or it
     * silently applies to one tier only.
     */
    tierMinSeats: integer().notNull().default(1),
    tierMaxSeats: integer(),
    description: text(),
    type: PriceTypeEnum('type').notNull(),
    extraServices: varchar({ length: 256 }).array(),
    isActive: boolean().notNull().default(false),
    isInternal: boolean().notNull().default(false),
    isFavorite: boolean().notNull().default(false),
    createdByUserId: text().references(() => user.id, { onDelete: 'set null' }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_price_type').on(t.type),
    index('idx_price_duration').on(t.duration),
    // The bracket resolver reads active public rows by duration then filters on the tier bounds.
    index('idx_price_tier').on(t.duration, t.tierMinSeats),
    index('idx_price_is_active').on(t.isActive),
    index('idx_price_is_internal').on(t.isInternal),
    index('idx_price_is_favorite').on(t.isFavorite),
    index('idx_price_created_at').on(t.createdAt),
    index('idx_price_updated_at').on(t.updatedAt),
  ],
)

export const priceRelations = relations(Price, ({ one }) => ({
  createdByUser: one(user, {
    fields: [Price.createdByUserId],
    references: [user.id],
  }),
}))

export const InsertPriceSchema = createInsertSchema(Price, {
  name: z.string().min(1).max(256),
  price: z.number().nonnegative(),
  duration: z.number().int().positive(),
  tierMinSeats: z.number().int().positive().default(1).optional(),
  tierMaxSeats: z.number().int().positive().nullable().optional(),
  description: z.string().optional(),
  type: z.enum(PriceTypeEnum.enumValues),
  extraServices: z.array(z.string().max(256)).optional(),
  isActive: z.boolean().default(false).optional(),
  isInternal: z.boolean().default(false).optional(),
  isFavorite: z.boolean().default(false).optional(),
  createdByUserId: z.string().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdatePriceSchema = createUpdateSchema(Price, {
  name: z.string().min(1).max(256),
  price: z.number().nonnegative(),
  duration: z.number().int().positive(),
  tierMinSeats: z.number().int().positive(),
  tierMaxSeats: z.number().int().positive().nullable(),
  description: z.string(),
  type: z.enum(PriceTypeEnum.enumValues),
  extraServices: z.array(z.string().max(256)),
  isActive: z.boolean(),
  isInternal: z.boolean(),
  isFavorite: z.boolean(),
  createdByUserId: z.string(),
})
  .omit({
    id: true,
    createdByUserId: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectPriceSchema = createSelectSchema(Price, {
  id: z.uuid(),
  name: z.string(),
  price: z.number(),
  duration: z.number().int(),
  tierMinSeats: z.number().int(),
  tierMaxSeats: z.number().int().nullable(),
  description: z.string().nullable(),
  type: z.enum(PriceTypeEnum.enumValues),
  extraServices: z.array(z.string()).nullable(),
  isActive: z.boolean(),
  isInternal: z.boolean(),
  isFavorite: z.boolean(),
  createdByUserId: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertPrice = typeof Price.$inferInsert
export type UpdatePrice = z.infer<typeof UpdatePriceSchema>
export type SelectPrice = typeof Price.$inferSelect
